/**
 * @file VncTile.tsx
 * @description Componente que renderiza uma única tela VNC de um host, estabelecendo
 * a conexão via WebSocket (novnc/RFB) e expondo seu estado de conexão e controles
 * (ex.: tela cheia) para o componente pai {@link VncPage}.
 */

import React, { useEffect, useRef, useState } from 'react';
import RFB from 'novnc-core';
import { AppIcon } from '../utils/AppIcon';
import { toast } from 'react-toastify';

/** Senha fixa utilizada para autenticar nas sessões VNC. */
const VNC_PASSWORD = "@brasil123";

/** Representa um host VNC disponível para conexão. */
export type VncHost = {
  /** Tipo da tarefa associada ao host (opcional). */
  type_task?: string;
  /** Identificador único da tarefa/host. */
  task_id: string;
  /** Status reportado pelo backend (`1` = online). */
  status?: number;
  /** Porta do serviço websockify do host. */
  port: string;
  /** Endereço IP do host. */
  ip: string;
  /** Nome do host. */
  hostname: string;
};

/** Estados possíveis de uma conexão VNC. */
export type ConnStatus = "connecting" | "connected" | "idle";

/** Controles imperativos expostos por um tile ao componente pai. */
export type TileControls = { fullscreen: () => void };

/** Propriedades do componente {@link VncTile}. */
type VncTileProps = {
  /** Host a ser exibido. */
  host: VncHost;
  /** Quando `true`, a sessão é apenas para visualização (sem interação de teclado/mouse). */
  viewOnly: boolean;
  /** Indica que este é o tile em foco (Modo Único), aplicando estilo de destaque. */
  focused?: boolean;
  /** Modo compacto (Modo Grade): reduz qualidade e desativa o redimensionamento da sessão. */
  compact?: boolean;
  /** Quando `false`, a sessão VNC é encerrada e o tile fica ocioso. Padrão: `true`. */
  active?: boolean;
  /** Altura do canvas de renderização (px ou valor CSS). */
  canvasHeight: number | string;
  /** Callback disparado ao clicar no tile. */
  onSelect?: (host: VncHost) => void;
  /** Callback de mudança de estado de conexão (`null` ao desmontar). */
  onStatus?: (taskId: string, status: ConnStatus | null) => void;
  /** Registra/desregistra os controles imperativos do tile no pai. */
  registerControls?: (taskId: string, controls: TileControls | null) => void;
  /** Callback para alternar a conexão do tile (botão conectar/desconectar). */
  onToggleConnection?: (host: VncHost) => void;
};

/** Metadados visuais (cor, ícone, rótulo e pulsação) associados a cada estado de conexão. */
const STATUS_META: Record<ConnStatus, { color: string; icon: string; label: string; pulse?: boolean }> = {
  connecting: { color: "#f5a623", icon: "sync", label: "Conectando...", pulse: true },
  connected: { color: "#2E8B57", icon: "live_tv", label: "Conectado" },
  idle: { color: "#888", icon: "tv_off", label: "Desconectado" },
};

/**
 * Renderiza a tela VNC de um host.
 *
 * Gerencia o ciclo de vida da conexão RFB: cria a sessão ao montar/ativar, ajusta
 * qualidade e modo somente-leitura conforme as props, reporta o estado de conexão
 * via {@link VncTileProps.onStatus} e expõe o controle de tela cheia através de
 * {@link VncTileProps.registerControls}.
 *
 * @param props Propriedades do tile. Ver {@link VncTileProps}.
 * @returns Elemento React com o cabeçalho de status e o canvas da sessão VNC.
 */
const VncTile = ({
  host,
  viewOnly,
  focused,
  compact,
  active = true,
  canvasHeight,
  onSelect,
  onStatus,
  registerControls,
  onToggleConnection,
}: VncTileProps) => {

  /** Elemento DOM que hospeda o canvas renderizado pelo RFB. */
  const containerRef = useRef<any>(null);
  /** Instância atual da conexão RFB (novnc), ou `null` quando não há sessão ativa. */
  const rfbRef = useRef<any>(null);

  const [status, setStatus] = useState<ConnStatus>(active ? "connecting" : "idle");
  /** Indica que o endereço foi copiado recentemente, para feedback visual temporário. */
  const [copied, setCopied] = useState(false);

  /** Copia o endereço `ip:porta` do host para a área de transferência. */
  const copyAddress = (e: React.MouseEvent) => {

    e.stopPropagation();

    const address = `${host.ip}:${host.port}`;

    navigator.clipboard.writeText(address).then(() => {

      setCopied(true);

      toast.success(`IP ${address} copiado`, {});

      setTimeout(() => setCopied(false), 1500);

    }).catch(() => { });

  };

  // Sincroniza o modo somente-leitura da sessão ativa quando `viewOnly` muda.
  useEffect(() => {

    if (rfbRef.current) rfbRef.current.viewOnly = viewOnly;

  }, [viewOnly]);

  // Ajusta qualidade/compressão/redimensionamento ao alternar entre os modos compacto e expandido.
  useEffect(() => {

    const rfb = rfbRef.current;

    if (!rfb) return;

    rfb.qualityLevel = compact ? 3 : 6;
    rfb.compressionLevel = compact ? 9 : 2;
    rfb.resizeSession = !compact;

  }, [compact]);

  // Propaga cada mudança de estado de conexão para o componente pai.
  useEffect(() => {

    if (onStatus) onStatus(host.task_id, status);

  }, [status]);

  // Ciclo de vida da conexão RFB: cria/encerra a sessão conforme o host e o estado `active`.
  useEffect(() => {

    let cancelled = false;

    if (!active) {

      if (containerRef.current) containerRef.current.innerHTML = "";

      setStatus("idle");

      if (onStatus) onStatus(host.task_id, "idle");

      return () => { };

    }

    if (containerRef.current) containerRef.current.innerHTML = "";
    setStatus("connecting");

    try {

      const rfb = new RFB(containerRef.current, `ws://${host.ip}:${host.port}/websockify`, { credentials: { password: VNC_PASSWORD }, shared: true, repeaterID: "" });

      rfbRef.current = rfb;

      rfb.viewOnly = viewOnly;
      rfb.scaleViewport = true;
      rfb.resizeSession = !compact;
      rfb.qualityLevel = compact ? 3 : 6;
      rfb.compressionLevel = compact ? 9 : 2;
      rfb.background = "#0b0b0b";

      rfb.addEventListener("connect", () => {
        if (cancelled) return;
        setStatus("connected");
      });

      rfb.addEventListener("disconnect", () => {
        if (cancelled) return;
        rfbRef.current = null;
        setStatus("idle");
      });

      rfb.addEventListener("securityfailure", () => {
        if (cancelled) return;
        setStatus("idle");
      });

    } catch (exc) {
      setStatus("idle");
    }

    if (registerControls) {
      registerControls(host.task_id, {

        /** Solicita a exibição do canvas em tela cheia, com fallbacks por navegador. */
        fullscreen: () => {

          const elem = containerRef.current;

          if (!elem) return;
          if (elem.requestFullscreen) elem.requestFullscreen();
          else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
          else if (elem.msRequestFullscreen) elem.msRequestFullscreen();

        },

      });

    }

    // Cleanup: encerra a sessão RFB e desregistra controles/estado ao desmontar ou reconectar.
    return () => {

      cancelled = true;

      if (rfbRef.current) {

        try { rfbRef.current.disconnect(); } catch (e) { }

        rfbRef.current = null;

      }

      if (registerControls) registerControls(host.task_id, null);
      if (onStatus) onStatus(host.task_id, null);

    };

  }, [host.task_id, host.ip, host.port, active]);

  /** Metadados visuais correspondentes ao estado de conexão atual. */
  const meta = STATUS_META[status];
  const statusLabel = meta.label;

  return (
    <div
      className={`vnc-tile${focused ? " focused" : ""}`}
      onClick={() => onSelect && onSelect(host)}
    >
      <div className="vnc-tile-header">
        <span className={`vnc-status-dot${meta.pulse ? " pulse" : ""}`} style={{ background: meta.color }} />
        <span
          className="vnc-tile-title vnc-tile-title-copy"
          onClick={copyAddress}
          title={copied ? "Copiado!" : "Clique para copiar"}
        >
          {host.ip}:{host.port}
          <AppIcon name={copied ? "check" : "content_copy"} size={13} className="vnc-tile-copy-icon" color='#3579ea' />
        </span>
        {/* <span className="vnc-tile-status" style={{ color: meta.color }}>{statusLabel}</span> */}
        {onToggleConnection && (
          <AppIcon
            name={active ? "desktop_access_disabled" : "play_circle"}
            size={18}
            color={active ? "#d95050" : "#2E8B57"}
            className="vnc-tile-action"
            onClick={(e) => { e.stopPropagation(); onToggleConnection(host); }}
          />
        )}
      </div>

      <div className="vnc-tile-body">
        <div ref={containerRef} className="vnc-tile-canvas" style={{ height: canvasHeight }} />
        {status !== "connected" && (
          <div className="vnc-tile-overlay">
            <AppIcon name={meta.icon} size={28} style={{ color: meta.color }} />
            <span>{statusLabel}</span>
            {status === "idle" && (
              <span style={{ fontSize: 11, opacity: 0.7 }}>{host.ip}:{host.port}</span>
            )}
          </div>
        )}
      </div>

      <div className="vnc-tile-footer">
        <span className="vnc-title-footer">{host.hostname}</span>
      </div>

    </div>
  );

};

export default VncTile;
